import { useState, useEffect } from "react";
import axios from "axios";
import {
  Loader2, CheckCircle2, XCircle, Clock, AlertCircle, User,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const STATUS_STYLES = {
  PENDING: "bg-amber-100 text-amber-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-red-100 text-red-700",
};

export default function LMSApprovals() {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [comment, setComment] = useState("");

  useEffect(() => { fetchApprovals(); }, []);

  const fetchApprovals = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/lms/pending-approvals`, {
        headers: getHeaders(), withCredentials: true,
      });
      setApplications(res.data.pending_approvals || []);
    } catch {}
    setLoading(false);
  };

  const handleApprove = async (appId) => {
    setActionLoading(appId);
    try {
      await axios.post(`${API}/lms/approve/${appId}`, null, {
        params: { comments: comment },
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Application approved");
      setComment("");
      fetchApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
    setActionLoading(null);
  };

  const handleReject = async (appId) => {
    if (!comment.trim()) {
      toast.error("Please provide a reason for rejection");
      return;
    }
    setActionLoading(appId);
    try {
      await axios.post(`${API}/lms/reject/${appId}`, null, {
        params: { reason: comment },
        headers: getHeaders(), withCredentials: true,
      });
      toast.success("Application rejected");
      setComment("");
      fetchApprovals();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
    setActionLoading(null);
  };

  if (loading) {
    return <div className="p-6 flex items-center justify-center min-h-[400px]">
      <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
    </div>;
  }

  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-lg font-semibold text-slate-800">Pending Approvals</h2>
        {applications.length > 0 && (
          <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-none">
            {applications.length} pending
          </Badge>
        )}
      </div>

      {applications.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm bg-white border border-slate-200">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          <p>No pending approvals</p>
          <p className="text-[10px] mt-1">All caught up!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {applications.map((app) => (
            <div key={app.id} className="bg-white border border-slate-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center">
                      <User className="w-3.5 h-3.5 text-emerald-700" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-800">{app.employee_name}</p>
                      <p className="text-[10px] text-slate-400">{app.application_number}</p>
                    </div>
                  </div>
                  <div className="mt-2 ml-9 space-y-1">
                    <p className="text-xs text-slate-600">
                      <span className="font-medium">{app.leave_type_name}</span>
                      {" "}&middot; {app.total_days} day{app.total_days !== 1 ? "s" : ""}
                    </p>
                    <p className="text-[10px] text-slate-400 flex items-center gap-1">
                      <Clock className="w-2.5 h-2.5" />
                      {app.start_date} to {app.end_date}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action buttons */}
              <div className="mt-3 ml-9 space-y-2">
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)}
                  rows={2} className="text-xs rounded-none" placeholder="Comments (required for rejection)..." />
                <div className="flex items-center gap-2">
                  <Button size="sm" onClick={() => handleApprove(app.id)} disabled={actionLoading === app.id}
                    className="h-8 text-xs rounded-none bg-emerald-600 hover:bg-emerald-500 gap-1">
                    {actionLoading === app.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                    Approve
                  </Button>
                  <Button size="sm" onClick={() => handleReject(app.id)} disabled={actionLoading === app.id}
                    variant="outline" className="h-8 text-xs rounded-none text-red-500 border-red-200 hover:bg-red-50 gap-1">
                    <XCircle className="w-3.5 h-3.5" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && applications.length > 0 && (
        <div className="mt-4">
          <Button onClick={fetchApprovals} variant="outline" size="sm"
            className="h-7 text-xs rounded-none">
            Refresh
          </Button>
        </div>
      )}
    </div>
  );
}
